import { FC, useState, useEffect } from 'react'
import cn from 'classnames'
import { useLocation } from 'react-router-dom'

import MessageInput from '@/MessageInput'
import { useMessenger } from '@/hooks/useMessenger'
import { useCheckMobile } from '@/hooks/useCheckMobile'
import ChatThread from '@/ChatThread'
import QuickReply from '@/components/QuickReply'

import { MESSAGE_TYPES } from '@/context/constants'

import './styles.css'

interface ChatWidgetOpenLocationState {
  chatWidgetOpen: boolean
}

const ENABLE_INPUT_COUNT = 2

const ChatWidget: FC = () => {
  const location = useLocation()
  const {
    getIsInit,
    sendMessage,
    getConfig,
    getSelectedConversation,
    getConversationMessages,
    getLastConversationMessage,
    findConversation,
    getLoading,
    getUserObj,
  } = useMessenger()
  const isInit = getIsInit()
  const [openWidget, setOpenWidget] = useState(false)
  const [isInputDisabled, setInputDisabled] = useState(true)
  const selectedConvoId = getSelectedConversation()
  const lastConversationMessage = getLastConversationMessage()
  const isMobile = useCheckMobile()
  const loading = getLoading()
  const userObj = getUserObj()

  const toggleWidget = () => setOpenWidget(prev => !prev)

  useEffect(() => {
    sendMessage({
      type: MESSAGE_TYPES.SUBSCRIBE_USER,
      userExternalId: getConfig()?.userExternalId,
    })
    sendMessage({
      type: MESSAGE_TYPES.GET_CONVERSATIONS,
      userExternalId: getConfig()?.userExternalId,
    })
  }, [])

  useEffect(() => {
    // Check if state is open
    if (isInit) {
      const initOpenState =
        (location?.state as ChatWidgetOpenLocationState)?.chatWidgetOpen ||
        false
      setOpenWidget(initOpenState)
    }
  }, [isInit, location])

  useEffect(() => {
    const conversation = findConversation(getSelectedConversation())
    const messages = getConversationMessages(getSelectedConversation())

    if (
      messages?.length > ENABLE_INPUT_COUNT &&
      conversation?.metadata?.state !== 'CLOSED'
    ) {
      setInputDisabled(false)
    } else {
      setInputDisabled(true)
    }
  }, [lastConversationMessage])

  useEffect(() => {
    if (openWidget && !loading.conversations) {
      sendMessage({
        type: MESSAGE_TYPES.NEW_CONVERSATION,
        userExternalId: getConfig()?.userExternalId,
      })
    }
  }, [openWidget, loading.conversations])

  useEffect(() => {
    if (selectedConvoId && openWidget) {
      sendMessage({
        type: MESSAGE_TYPES.SUBSCRIBE_CONVERSATION,
        conversationId: selectedConvoId,
        userExternalId: getConfig()?.userExternalId,
      })
      sendMessage({
        type: MESSAGE_TYPES.GET_MESSAGES,
        conversationId: selectedConvoId,
        userExternalId: getConfig()?.userExternalId,
      })
    }
  }, [selectedConvoId, openWidget])

  const renderChatThread = () => {
    const messages = getConversationMessages(getSelectedConversation())

    return (
      <ChatThread
        messages={messages}
        className="pmui-h-full"
        isChatWidget={true}
      />
    )
  }

  if (loading.conversations) {
    return null
  }

  return (
    <>
      <button
        className={cn(
          'fab-btn pmui-flex pmui-justify-center pmui-items-center \
          pmui-overflow-hidden pmui-rounded-full pmui-text-white',
          { 'pmui-hidden': isMobile && openWidget },
        )}
        onClick={() => toggleWidget()}
      >
        <span
          className={cn(
            'icon pmui-text-2xl ',
            { 'icon-ic-message-selected icon-animation': !openWidget },
            {
              'icon-close-round close-icon-animation': openWidget,
            },
          )}
        ></span>
      </button>
      <div
        className={cn(
          { 'chat-wrapper': !isMobile },
          {
            'chat-wrapper-open': openWidget,
          },
          {
            'chat-wrapper-closed': !openWidget,
          },
          { 'chat-wrapper-mobile': isMobile },
        )}
      >
        <div className="chat-header">
          <div className="pmui-flex pmui-justify-between">
            <div
              className={cn(
                'sm:pmui-text-3xl xs:pmui-text-2xl pmui-font-semibold',
                {
                  'pmui-text-2xl': isMobile,
                },
              )}
            >
              <div className="pmui-flex pmui-gap-2">
                {/* <div className="pmui-w-10 pmui-h-10 pmui-rounded-full pmui-flex pmui-justify-center pmui-items-center pmui-bg-slate-600">
                  <span className="icon icon-ic-poplar pmui-text-lg" />
                </div> */}
                <div>{`Hi ${userObj?.firstName}!`}</div>
                <div className="wave">👋</div>
              </div>
            </div>
            <div
              className={cn(
                'icon icon-close-round pmui-text-2xl pmui-text-pop-gray-100 pmui-cursor-pointer',
                {
                  'pmui-hidden': !isMobile,
                },
                {
                  'pmui-text-xl': isMobile,
                },
              )}
              onClick={() => toggleWidget()}
            />
          </div>
          <div
            className={cn(
              'sm:pmui-text-base xs:pmui-text-sm pmui-text-pop-gray-50 pmui-pt-1',
              { 'pmui-text-sm': isMobile },
            )}
          >
            Need help? Select a category to get started
          </div>
        </div>
        <div className="chat-container-pmui">
          <div
            className={cn('messages-container', {
              'messages-container-qr':
                lastConversationMessage?.content?.type === 'quick_reply',
            })}
          >
            {renderChatThread()}
          </div>

          {lastConversationMessage?.content?.type === 'quick_reply' ? (
            <div className={cn('pmui-m-3 pmui-text-xs')}>
              <QuickReply message={lastConversationMessage} />
            </div>
          ) : (
            <div className="pmui-my-2" />
          )}
          <div className="pmui-pb-10 ">
            <MessageInput
              disabled={isInputDisabled}
              containerClassName={cn(
                'pmui-rounded-lg pmui-absolute pmui-bottom-0 pmui-right-0 pmui-w-full',
                {
                  'pmui-z-50': openWidget,
                },
                {
                  'pmui-z-0': !openWidget,
                },
              )}
              buttonClassName={cn({
                'pmui-w-14': isMobile,
              })}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default ChatWidget
